import {
  AssetType,
  Battery,
  BatteryDataType,
  getAssetConfiguration,
  getDefaultBatteryData,
  getDefaultMotorData,
  getDefaultPVData,
  Motor,
  MotorDataType,
  OptimizationParameterValue,
  PVSystem,
  PVSystemDataType,
  SaveBar,
} from '@e2m-customer-portal/domains';
import { useOptimizationGroupDataUpdate, useSnackbar } from '@e2m-customer-portal/hooks';
import { Nullable } from '@e2m-customer-portal/shared';
import { AbortConfirmationDialog } from '@e2m-customer-portal/ui';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface OptimizationGroupItemsProps {
  data: BatteryDataType[] | MotorDataType[];
  pvData: Nullable<PVSystemDataType[]>;
  groupId: string;
  assetType?: AssetType;
  readOnly: (parameter?: string) => boolean;
}

export function OptimizationGroupItems({
  data,
  pvData,
  groupId,
  assetType,
  readOnly,
}: Readonly<OptimizationGroupItemsProps>): ReactElement<OptimizationGroupItemsProps> {
  const [t] = useTranslation();

  const { showError, showSuccess } = useSnackbar();
  const [resetTrigger, setResetTrigger] = useState(false);
  const [saveBarOpen, setSaveBarOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [update] = useOptimizationGroupDataUpdate();

  const [updatedItemsData, setUpdatedItemsData] = useState<Map<string, Map<string, OptimizationParameterValue>>>(
    new Map(),
  );
  const [updatedPVsData, setUpdatedPVsData] = useState<
    Map<string, Map<string, OptimizationParameterValue>> | undefined
  >(pvData ? new Map() : undefined);

  const updateItems = () => {
    const asset_configuration =
      assetType && updatedItemsData && getAssetConfiguration(assetType, updatedItemsData, updatedPVsData);

    const mutationBody = JSON.stringify({
      asset_type: assetType,
      ...asset_configuration,
    });

    setIsSaving(true);
    update(groupId, mutationBody)
      .then((res) => {
        if (res.data) {
          showSuccess(t('COPT_EDIT_SUCCESS_MESSAGE'));
          setSaveBarOpen(false);
        }
      })
      .catch((error) => {
        showError(error);
      })
      .finally(() => {
        setIsSaving(false);
        setUpdatedItemsData(new Map());
        setUpdatedPVsData(new Map());
      });
  };

  if (!data || data.length < 1)
    return <div className="font-medium test-lg p-4">{t('COPT_MISSING_PARAMETERS_MESSAGE_GROUP')}</div>;

  return (
    <>
      {assetType !== AssetType.Biogas ? (
        <>
          {assetType === AssetType.CoLocation && pvData && (
            <>
              <h2 className="my-0 font-normal text-lg p-6">{t('COPT_COLOCATION_PV_TITLE')}</h2>
              <div className="mx-6 mb-10 grid grid-cols-1 xl:grid-cols-[repeat(auto-fill,_minmax(64rem,_1fr))] gap-2">
                {Object.entries(pvData)
                  .sort((a, b) => a[1].device_number - b[1].device_number)
                  .map(([key, pv]) => (
                    <PVSystem
                      key={key}
                      identifier={key}
                      data={getDefaultPVData(pv)}
                      updatedPVsData={updatedPVsData}
                      setUpdatedPVsData={setUpdatedPVsData}
                      setSaveBarOpen={setSaveBarOpen}
                      resetTrigger={resetTrigger}
                      assetType={assetType}
                      readOnly={readOnly}
                    />
                  ))}
              </div>
            </>
          )}
          {assetType !== AssetType.Danpower && (
            <>
              <h2 className="my-0 font-normal text-lg p-6">{t('COPT_BATTERIES')}</h2>
              <div className="mx-6 mb-10 grid grid-cols-1 xl:grid-cols-[repeat(auto-fill,_minmax(32rem,_1fr))] gap-2">
                {Object.entries(data)
                  .sort((a, b) => a[1].device_number - b[1].device_number)
                  .map(([key, battery]) => (
                    <Battery
                      key={key}
                      identifier={key}
                      data={getDefaultBatteryData(battery)}
                      updatedItemsData={updatedItemsData}
                      setUpdatedItemsData={setUpdatedItemsData}
                      setSaveBarOpen={setSaveBarOpen}
                      resetTrigger={resetTrigger}
                      setIsValid={setIsValid}
                      assetType={assetType}
                      readOnly={readOnly}
                    />
                  ))}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <h2 className="my-0 font-normal text-lg px-6">{t('COPT_MOTORS')}</h2>
          <div className="mx-6 mb-10 grid grid-cols-1 xl:grid-cols-[repeat(auto-fill,_minmax(32rem,_1fr))] gap-2">
            {Object.entries(data)
              .sort((a, b) => a[1].device_number - b[1].device_number)
              .map(([key, motor]) => (
                <Motor
                  key={key}
                  identifier={key}
                  data={getDefaultMotorData(motor)}
                  updatedItemsData={updatedItemsData}
                  setUpdatedItemsData={setUpdatedItemsData}
                  setSaveBarOpen={setSaveBarOpen}
                  resetTrigger={resetTrigger}
                  setIsValid={setIsValid}
                  readOnly={readOnly}
                />
              ))}
          </div>
        </>
      )}
      {!readOnly() && (
        <>
          <SaveBar
            open={saveBarOpen}
            saving={isSaving}
            isValid={isValid}
            onCancel={() => setResetTrigger(!resetTrigger)}
            onSave={updateItems}
          />
          <AbortConfirmationDialog when={saveBarOpen} />
        </>
      )}
    </>
  );
}
